import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useThemeContext } from '../contexts/ThemeContext';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import { useAppContext } from '../contexts/Storage';
import RecordItemCard from '../components/Record/RecordItemCard';
import ManagePermissionsModal from '../components/Record/ManagePermissionsModal';
import CreateRecordModal from '../components/Record/CreateRecordModal';
import EditRecordModal from '../components/Record/EditRecordModal';
import JoinRecordModal from '../components/Record/JoinRecordModal';
import ImportRecordModal from '../components/Record/ImportRecordModal';
import DeleteRecordModal from '../components/Record/DeleteRecordModal';
import { Loading } from '../components/utils';
import { useAuth } from '../hooks/useAuth';
import { RecordItem } from '../models/RecordItem';
import plusIcon from '../assets/images/plus.png';
import searchIcon from '../assets/images/search.png';
import emptyFolderIcon from '../assets/images/empty_folder.png';
import editIcon from '../assets/images/edit.png';
import editIconDark from '../assets/images-night/edit.png';
import deleteIcon from '../assets/images/delete.png';
import deleteIconDark from '../assets/images-night/delete.png';
import userIcon from '../assets/images/user.png';
import downloadIcon from '../assets/images/download.png';
import downloadIconDark from '../assets/images-night/download.png';
import filesIcon from '../assets/images/files.png';
import shareIcon from '../assets/images/share.png';
import shareIconDark from '../assets/images-night/share.png';
import usersIcon from '../assets/images/user.png'; // Reusing user icon for permissions
import { Button, Input } from '../components/ui';
import { calculateMenuPosition } from '../utils/menuPosition';
import { useRecords } from '../hooks/useRecords';
import { FROM_SETUP_KEY, markOnboarding } from '../onboarding/onboardingStorage';

const RecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDark } = useThemeContext();
  const { setNavLinks } = useNavLinksContext();
  const { user, setSelectedRecord } = useAppContext();
  const { sendVerificationCode } = useAuth();
  const { 
    records, 
    isLoading, 
    fetchRecords, 
    createRecord, 
    updateRecord,
    deleteRecord,
    joinRecordByCode,
    exportRecord,
    importRecord,
    isCreating,
    isImporting,
    isJoining,
    isUpdating,
    isDeleting
  } = useRecords();
  
  const [showMenu, setShowMenu] = useState<{ x: number, y: number, record: RecordItem } | null>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState<RecordItem | null>(null);
  const [showCreateRecordModal, setShowCreateRecordModal] = useState(false);
  const [showEditRecordModal, setShowEditRecordModal] = useState<RecordItem | null>(null);
  const [showJoinRecordModal, setShowJoinRecordModal] = useState(false);
  const [showImportRecordModal, setShowImportRecordModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; record: RecordItem | null }>({ 
    isOpen: false, 
    record: null 
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Set different navigation links for the Records page
  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/records', label: 'Books' },
      ],
    });

    // Cleanup: restore default links when leaving the page
    return () => {
      setNavLinks({
        links: [
          { to: '/dashboard', label: 'Dashboard' },
          { to: '/records', label: 'Books' },
        ],
      });
    };
  }, [setNavLinks]);

  // Fetch records on component mount
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // First-run: open create books if they have none
  useEffect(() => {
    if (isLoading) return;
    if (searchParams.get('setup') !== '1') return;
    if (records.length === 0) {
      setShowCreateRecordModal(true);
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [isLoading, records.length, searchParams, setSearchParams]);

  // Auto-select first record when records are loaded
  useEffect(() => {
    if (records.length > 0) {
      setSelectedRecord(records[0]);
    }
  }, [records, setSelectedRecord]);

  // Get icon based on theme
  const getIcon = (lightIcon: string, darkIcon: string) => {
    return isDark ? darkIcon : lightIcon;
  };

  // Filter records based on search query
  const filteredRecords = useMemo(() => {
    if (!searchQuery.trim()) {
      return records;
    }

    const query = searchQuery.toLowerCase();
    return records.filter(record => {
      // Search by record name
      if (record.record_name?.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search by record description
      if (record.record_description?.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search by invite code
      if (record.record_invite_code?.toString().includes(query)) {
        return true;
      }
      
      // Search by currency code
      if (record.record_currency_code?.toLowerCase().includes(query)) {
        return true;
      }
      
      return false;
    });
  }, [records, searchQuery]);

  const handleMenuClick = (event: React.MouseEvent, record: RecordItem) => {
    // Calculate optimal position for the menu
    const menuPosition = calculateMenuPosition(event.clientX, event.clientY, 200, 350);

    setShowMenu({
      x: menuPosition.x,
      y: menuPosition.y,
      record
    });
  };

  const handleMenuClose = () => {
    setShowMenu(null);
  };

  const handleMenuAction = async (action: string, record: RecordItem) => {
    switch (action) {
      case 'edit':
        setShowEditRecordModal(record);
        break;
      case 'delete':
        setDeleteConfirmation({ isOpen: true, record });
        break;
      case 'join':
        setShowJoinRecordModal(true);
        break;
      case 'export':
        const exportResult = await exportRecord(record.id!);
        if (exportResult.success) {
          toast.success('Record exported successfully');
        }
        break;
      case 'import':
        setShowImportRecordModal(true);
        break;
      case 'share':
        // Share/copy invite code
        if (record.record_invite_code) {
          navigator.clipboard.writeText(record.record_invite_code.toString());
          toast.success('Invite code copied to clipboard');
        } else {
          toast.info('No invite code available for this record');
        }
        break;
      case 'permissions':
        // Show permissions modal
        setShowPermissionsModal(record);
        break;
    }
    handleMenuClose();
  };

  const handleCreateNewRecord = () => {
    setShowCreateRecordModal(true);
  };

  const handleCreateRecord = async (recordData: Partial<RecordItem>) => {
    try {
      const result = await createRecord({
        record_name: recordData.record_name || '',
        record_description: recordData.record_description || '',
        record_currency_code: recordData.record_currency_code || 'USD'
      });

      if (result.success && result.data) {
        setShowCreateRecordModal(false);
        markOnboarding(user?.id, 'namedBooks');
        setSelectedRecord(result.data);
        sessionStorage.setItem(FROM_SETUP_KEY, '1');
        navigate('/records/transactions/new');
      }
    } catch (error) {
      console.error('Failed to create record:', error);
      toast.error('Failed to create your books');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmation({ isOpen: false, record: null });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleRecordClick = (record: RecordItem) => {
    setSelectedRecord(record);
    navigate('/records/transactions');
  };

  return (
    <div className={`h-shink-from-navbar h-fill-from-navbar lg:pl-2 bg-bg_grey_light text-dark overflow-y-scroll`}>
      <div className="">
        {/* Header and title - Mobile responsive */}
        <div className="p-6 pb-3 bg-light rounded-t-lg mb-2 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="w-full sm:w-auto">
            <h3 className="text-xl mx-0 sm:mx-4">Books</h3>
            <p className="text-grey_dark mx-0 sm:mx-4 mt-1 text-sm sm:hidden md:block">One set of books for a shop, personal use, or a client</p>
          </div>
          <div data-tour="records-new">
            <Button
              onClick={handleCreateNewRecord}
              variant="filled-primary"
              className="flex items-center text-white w-full sm:w-auto justify-center"
              disabled={isCreating || isLoading}
            >
            <img
              src={plusIcon}
              alt="Add"
              className="object-contain w-4 h-4 mr-2"
            />
            {isCreating ? 'Creating...' : (isLoading ? 'Loading...' : 'New books')}
            </Button>
          </div>
        </div>

        {/* Records grid */}
        <div className="p-4 sm:p-6 h-fill-from-navbar bg-light rounded-lg">
          {/* Search section - Mobile responsive */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h4 className="text-lg">Your books ({filteredRecords.length})</h4>
            <div data-tour="records-search" className="flex items-center w-full sm:w-auto gap-4">
              <Input
                type='text'
                leftIcon={searchIcon}
                attributes={{
                  placeholder: "Search books",
                  value: searchQuery,
                  onChange: handleSearchChange,
                  disabled: isLoading
                }}
                className='w-full sm:w-64'
              />
            </div>
          </div>

          {isLoading && records.length === 0 ? (
            <Loading width="30px" height="30px" className="mx-2 mb-4" />
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <img
                src={emptyFolderIcon}
                alt="No records"
                className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 opacity-50"
              />
              <h5 className="text-lg mb-2">No books yet</h5>
              <p className="text-grey_dark mb-6 px-4">
                {searchQuery 
                  ? 'No books match your search' 
                  : 'Create your first books to start. Then add one sale or expense.'}
              </p>
              <Button 
                onClick={handleCreateNewRecord} 
                variant="filled-primary" 
                className="flex items-center mx-auto text-white"
                disabled={isLoading}
              >
                <img
                  src={plusIcon}
                  alt="Add"
                  className="object-contain w-4 h-4 mr-2"
                />
                New books
              </Button>
            </div>
          ) : (
            <div data-tour="records-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredRecords.map((record) => (
                <RecordItemCard
                  key={record.id}
                  record={record}
                  onMenuClick={handleMenuClick}
                  onClick={handleRecordClick}
                />
              ))}
            </div>
          )}
        </div>

        {/* Context Menu */}
        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={handleMenuClose}
            />
            <div
              className="fixed z-50 bg-light border border-grey_light rounded-lg shadow-lg py-2 min-w-[200px] max-w-[90vw]"
              style={{
                left: `${showMenu.x}px`,
                top: `${showMenu.y}px`,
                maxHeight: '70vh',
                overflowY: 'auto'
              }}
            >
              <button
                onClick={() => handleMenuAction('edit', showMenu.record)}
                className="w-full px-4 py-2 text-left hover:bg-bg_grey_light flex items-center"
              >
                <img src={getIcon(editIcon, editIconDark)} alt="Edit" className="w-6 h-6 object-contain mr-3" />
                Edit Record
              </button>
              <button
                onClick={() => handleMenuAction('delete', showMenu.record)}
                className="w-full px-4 py-2 text-left hover:bg-bg_grey_light flex items-center text-danger"
              >
                <img src={getIcon(deleteIcon, deleteIconDark)} alt="Delete" className="w-6 h-6 object-contain mr-3" />
                Delete Record
              </button>
              <button
                onClick={() => handleMenuAction('join', showMenu.record)}
                className="w-full px-4 py-2 text-left hover:bg-bg_grey_light flex items-center"
              >
                <img src={userIcon} alt="Join" className="w-6 h-6 p-1 object-contain mr-3" />
                Join Record
              </button>
              <button
                onClick={() => handleMenuAction('export', showMenu.record)}
                className="w-full px-4 py-2 text-left hover:bg-bg_grey_light flex items-center"
              >
                <img src={getIcon(downloadIcon, downloadIconDark)} alt="Export" className="w-6 h-6 object-contain mr-3" />
                Export Record
              </button>
              <button
                onClick={() => handleMenuAction('import', showMenu.record)}
                className="w-full px-4 py-2 text-left hover:bg-bg_grey_light flex items-center"
              >
                <img src={filesIcon} alt="Import" className="w-6 h-6 p-1 object-contain mr-3" />
                Import Record
              </button>
              <button
                onClick={() => handleMenuAction('share', showMenu.record)}
                className="w-full px-4 py-2 text-left hover:bg-bg_grey_light flex items-center"
              >
                <img src={getIcon(shareIcon, shareIconDark)} alt="Share" className="w-6 h-6 object-contain mr-3" />
                Share/Copy Invite Code
              </button>
              <div className="border-t border-grey_light my-2"></div>
              <button
                onClick={() => handleMenuAction('permissions', showMenu.record)}
                className="w-full px-4 py-2 text-left hover:bg-bg_grey_light flex items-center"
              >
                <img src={usersIcon} alt="Permissions" className="w-6 h-6 object-contain mr-3" />
                Permissions
              </button>
            </div>
          </>
        )}

        {/* Permissions Modal */}
        {showPermissionsModal && (
          <ManagePermissionsModal
            isOpen={true}
            onClose={() => setShowPermissionsModal(null)}
            recordId={showPermissionsModal.id!}
            recordName={showPermissionsModal.record_name || 'Untitled Record'}
          />
        )}

        {/* Create Record Modal */}
        {showCreateRecordModal && (
          <CreateRecordModal
            onClose={() => setShowCreateRecordModal(false)}
            onCreate={handleCreateRecord}
            isLoading={isLoading}
          />
        )}

        {/* Edit Record Modal */}
        {showEditRecordModal && (
          <EditRecordModal
            record={showEditRecordModal}
            onClose={() => setShowEditRecordModal(null)}
            onUpdate={async (id, data) => {
              if (!user?.id) {
                toast.error('User ID not found');
                return { success: false, error: 'User ID not found' };
              }
              const result = await updateRecord(id, {
                ...data,
                user_id: user.id
              });
              if (result.success) {
                setShowEditRecordModal(null);
              }
              return result;
            }}
            isLoading={isUpdating}
          />
        )}

        {/* Join Record Modal */}
        {showJoinRecordModal && (
          <JoinRecordModal
            onClose={() => setShowJoinRecordModal(false)}
            onJoin={async (code) => {
              const result = await joinRecordByCode(code);
              if (result.success) {
                setShowJoinRecordModal(false);
              }
              return result;
            }}
            isLoading={isJoining}
          />
        )}

        {/* Import Record Modal */}
        {showImportRecordModal && (
          <ImportRecordModal
            onClose={() => setShowImportRecordModal(false)}
            onImport={async (jsonData) => {
              const result = await importRecord(jsonData);
              if (result.success) {
                setShowImportRecordModal(false);
              }
              return result;
            }}
            isLoading={isImporting}
          />
        )}

        {/* Delete Record Modal (send code → enter OTP → delete) */}
        <DeleteRecordModal
          isOpen={deleteConfirmation.isOpen}
          record={deleteConfirmation.record}
          userEmail={user?.email ?? ''}
          creatorId={user?.id}
          onClose={handleDeleteCancel}
          onSendCode={async (email) => {
            const result = await sendVerificationCode(email);
            return {
              success: result.success,
              error: result.success ? undefined : result.error,
            };
          }}
          onConfirmDelete={async (recordId, creatorId, verificationCode) => {
            const result = await deleteRecord(recordId, creatorId, verificationCode);
            return {
              success: result.success,
              error: result.success ? undefined : result.error,
            };
          }}
          isDeleting={isDeleting}
        />
      </div>
    </div>
  );
};

export default RecordsPage;